import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { categories, articlesByCategory, type ArticleItem } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || '搞笑');
  const [articles, setArticles] = useState<ArticleItem[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setArticles(articlesByCategory[activeSlug] || []);
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const allCategories = categories;

  return (
    <>
      <ScrollToTop />
      <SEOSchema
        type="WebPage"
        data={{
          name: `香蕉视频 - ${activeCategory?.name || '分类'}`,
          description: activeCategory?.description || '浏览香蕉视频精彩内容分类',
          url: `https://whtefafuwu.cn/category/${activeSlug}`,
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://whtefafuwu.cn' },
            { '@type': 'ListItem', position: 2, name: activeCategory?.name || '分类' },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: activeCategory?.name || '分类' },
          ]}
        />

        <section className="w-full py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-2">
                {activeCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500">{activeCategory?.description}</p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {articles.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block"
                  >
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                        {article.duration}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors mb-1.5">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line"></i> {article.likes}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}